import { type DragEvent, type MouseEvent, type PointerEvent, useCallback, useRef } from "react";
import { isTauriRuntime } from "src/lib/runtime/environment";

/**
 * What a drag must not start from: somewhere the pointer means to do something else.
 *
 * Buttons are absent deliberately — a drag only begins once the pointer has moved, so a click on a
 * button still lands, and dragging the window from one is harmless.
 */
const NON_DRAGGABLE_ELEMENT_SELECTOR =
  "input, select, textarea, [contenteditable='true'], [data-no-window-drag]";

/** Far enough that the person meant to move the window rather than click what is under the pointer. */
const WINDOW_DRAG_THRESHOLD_PX = 4;

/**
 * Make a region behave like a title bar: drag to move the window, double-click to zoom.
 *
 * Preferred over `data-tauri-drag-region`, which Tauri matches against the element the pointer
 * actually hit. A header is full of layout — spacers, flex columns, the odd wrapper — and each one
 * swallows the drag unless it carries the attribute too, which is why headers here ended up
 * sprinkling it over every nested element and still left gaps. This asks the opposite question of
 * the target — is this something that is NOT draggable — so empty space anywhere in the region
 * works without being told about.
 *
 * Inert outside Tauri, where the page has no window to move.
 */
export function useWindowDragRegion() {
  const dragStartPoint = useRef<{ pointerId: number; x: number; y: number } | undefined>(undefined);

  const onPointerDownCapture = useCallback((event: PointerEvent<HTMLElement>) => {
    if (!isTauriRuntime() || event.button !== 0) return;

    const target = event.target;
    if (!(target instanceof Element) || target.closest(NON_DRAGGABLE_ELEMENT_SELECTOR)) return;

    dragStartPoint.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
  }, []);

  const onPointerMoveCapture = useCallback((event: PointerEvent<HTMLElement>) => {
    const startPoint = dragStartPoint.current;
    if (!startPoint || startPoint.pointerId !== event.pointerId || (event.buttons & 1) === 0) return;
    if (Math.hypot(event.clientX - startPoint.x, event.clientY - startPoint.y) < WINDOW_DRAG_THRESHOLD_PX) {
      return;
    }

    dragStartPoint.current = undefined;
    // Also stops the region's text being selected by what is now a window drag.
    event.preventDefault();
    event.stopPropagation();
    void import("@tauri-apps/api/window").then(({ getCurrentWindow }) =>
      getCurrentWindow()
        .startDragging()
        .catch(() => undefined)
    );
  }, []);

  const onPointerEndCapture = useCallback(() => {
    dragStartPoint.current = undefined;
  }, []);

  const onDragStartCapture = useCallback((event: DragEvent<HTMLElement>) => {
    if (!isTauriRuntime()) return;
    event.preventDefault();
  }, []);

  const onDoubleClickCapture = useCallback((event: MouseEvent<HTMLElement>) => {
    if (!isTauriRuntime() || event.button !== 0) return;

    const target = event.target;
    if (!(target instanceof Element) || target.closest(NON_DRAGGABLE_ELEMENT_SELECTOR)) return;

    dragStartPoint.current = undefined;
    event.preventDefault();
    event.stopPropagation();
    void import("@tauri-apps/api/window").then(({ getCurrentWindow }) =>
      getCurrentWindow()
        .toggleMaximize()
        .catch(() => undefined)
    );
  }, []);

  return {
    onPointerDownCapture,
    onPointerMoveCapture,
    onPointerUpCapture: onPointerEndCapture,
    onPointerCancelCapture: onPointerEndCapture,
    onDragStartCapture,
    onDoubleClickCapture,
  };
}
